/**
 * i18n — Internationalization framework
 *
 * Simple, lightweight i18n for Anyvio.
 * Supports English (default) and French.
 */

import { createContext, useContext, useState, useCallback } from 'react';
import en from './locales/en.json';
import fr from './locales/fr.json';

const locales = { en, fr };

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [locale, setLocale] = useState(() => {
    const stored = localStorage.getItem('anyvio_locale');
    if (stored && locales[stored]) return stored;
    const browserLang = navigator.language.split('-')[0];
    return locales[browserLang] ? browserLang : 'en';
  });

  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = locales[locale];
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        // Fallback to English
        value = locales.en;
        for (const ek of keys) {
          value = value?.[ek];
          if (value === undefined) return key; // Return key if not found
        }
        break;
      }
    }
    if (typeof value !== 'string') return key;

    // Replace {{param}} placeholders
    return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => params[paramKey] ?? `{{${paramKey}}}`);
  }, [locale]);

  const changeLocale = useCallback((newLocale) => {
    if (locales[newLocale]) {
      setLocale(newLocale);
      localStorage.setItem('anyvio_locale', newLocale);
    }
  }, []);

  return (
    <I18nContext.Provider value={{ locale, t, changeLocale, availableLocales: Object.keys(locales) }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};

export default I18nProvider;
